import { inject, Injectable } from '@angular/core';
import { BaseFlowsStore } from '@kinet-open-network/client/flows/data-access';
import {
  loadItemPrices,
  LoadItemPricesMode,
  ProductPricesService,
} from '@kinet-open-network/client/product-prices/data-access';
import { OrderStatus } from '@kinet-open-network/shared/data-access/model';
import { sumTotals } from '@kinet-open-network/shared/util';
import {
  FlowOrders,
  FlowOrdersStepData,
  itemShouldUsePrice,
} from './flow-orders.model';
import { FlowOrdersService } from './flow-orders.service';

@Injectable()
export class FlowOrdersStore extends BaseFlowsStore<
  FlowOrders,
  'id',
  FlowOrdersStepData
>(FlowOrdersService) {
  private readonly productPricesService = inject(ProductPricesService);
  override readonly service = inject(FlowOrdersService);

  override isTerminalState(status: string): boolean {
    return status === OrderStatus.finished;
  }

  /**
   * Loads prices for all priced items (customer + date, see `LoadItemPricesMode`),
   * then recalculates totals. Returns whether any price changed.
   */
  async refreshAllItemPrices(
    customerId: number,
    date: Date,
    mode: LoadItemPricesMode = 'effective',
  ): Promise<boolean> {
    const model = this.model();
    if (!model?.items?.length) return false;

    const { items, changed } = await loadItemPrices(model.items as any[], {
      service: this.productPricesService,
      customerId,
      date,
      mode,
      // unpriced lines (not full / complaint) and boxes keep their price
      skipItem: (item) => !itemShouldUsePrice(item) || !!item.stockItem?.box,
    });

    if (changed) {
      this.patchModel({ items, ...sumTotals(items) } as Partial<FlowOrders>);
    }
    return changed;
  }
}
